import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDatabase } from '../context/DatabaseContext';
import { useToast } from '../context/ToastContext';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { generateNLPQuery, getNLPQueries, executeQuery, generateChartsForNLPQuery, getNLPQueriesChartStatus, refineNLPQuery, updateNLPQuery, testRefinedQuery, getQueryExecutions } from '../services/api';
import { Sparkles, Eye, Play, X, Database, Grid, Database as DatabaseIcon, BarChart3, MessageSquare, History, Check, ChevronDown } from 'lucide-react';
import { formatLocalTime } from '../utils/timezone';
import { PlainQueryDisplay } from '../components/ui/PlainQueryDisplay';

export const QueryGenerator = () => {
    const { t } = useTranslation(['page-query-generator', 'common']);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { selectedDatabase, databases, setSelectedDatabase } = useDatabase();
    const { showToast } = useToast();
    const [question, setQuestion] = useState('');
    const [generating, setGenerating] = useState(false);
    const [loading, setLoading] = useState(false);
    const [nlpQueries, setNlpQueries] = useState([]);
    const [selectedQuery, setSelectedQuery] = useState(null);
    const [detailsModalOpen, setDetailsModalOpen] = useState(false);
    const [testModalOpen, setTestModalOpen] = useState(false);
    const [testParams, setTestParams] = useState({});
    const [queryResult, setQueryResult] = useState(null);
    const [executing, setExecuting] = useState(false);
    const [showResultView, setShowResultView] = useState('table'); // 'table' or 'json'
    const [generatingCharts, setGeneratingCharts] = useState(false);
    const [generatingChartForQuery, setGeneratingChartForQuery] = useState(null); // Track which query is being generated
    const [generatedChartQueryIds, setGeneratedChartQueryIds] = useState([]);
    
    // Chat refinement state
    const [chatModalOpen, setChatModalOpen] = useState(false);
    const [chatInstruction, setChatInstruction] = useState('');
    const [refiningQuery, setRefiningQuery] = useState(false);
    const [refinedQueryResult, setRefinedQueryResult] = useState(null);
    const [chatTextareaRef, setChatTextareaRef] = useState(null);
    
    // Test query state for chat modal
    const [testingRefinedQuery, setTestingRefinedQuery] = useState(false);
    const [refinedQueryTestResult, setRefinedQueryTestResult] = useState(null);
    const [refinedQueryTestParams, setRefinedQueryTestParams] = useState({});
    const [showRefinedQueryTestResult, setShowRefinedQueryTestResult] = useState('table');

    // Previous results state
    const [showPreviousResultsModal, setShowPreviousResultsModal] = useState(false);
    const [previousResults, setPreviousResults] = useState(null);
    const [loadingPreviousResults, setLoadingPreviousResults] = useState(false);
    const [queryExecutionsMap, setQueryExecutionsMap] = useState({}); // Map of queryId -> has executions

    const dbId = searchParams.get('db') || selectedDatabase?.id;

    // Filter databases to only show those with schema analysis completed
    const analyzedDatabases = databases.filter(db => 
        db.status && db.status.toLowerCase() === 'analyzed'
    );

    const handleDatabaseChange = (e) => {
        const dbId = e.target.value;
        const db = databases.find(db => db.id === parseInt(dbId));
        if (db) {
            setSelectedDatabase(db);
            // No toast notification for database switch
        }
    };

    useEffect(() => {
        if (dbId) {
            fetchNLPQueries();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dbId]);

    const fetchChartStatusForQueries = async () => {
        if (!dbId) return;

        try {
            const res = await getNLPQueriesChartStatus(dbId);
            const data = res.data?.data;

            if (data && data.queries) {
                const queryIdsWithCharts = data.queries
                    .filter(q => q.has_chart)
                    .map(q => q.query_id);
                setGeneratedChartQueryIds(queryIdsWithCharts);
            }
        } catch (error) {
            console.error('Failed to fetch chart status for queries:', error);
        }
    };

    const fetchNLPQueries = async () => {
        if (!dbId) return;
        setLoading(true);
        try {
            const res = await getNLPQueries(dbId);
            let nlpQueriesData = res.data?.data?.queries || [];

            setNlpQueries(nlpQueriesData);

            // Fetch chart status for the loaded queries
            await fetchChartStatusForQueries();

            // Check which queries have previous executions
            const executionsMap = {};
            await Promise.all(
                nlpQueriesData.map(async (query) => {
                    try {
                        const response = await getQueryExecutions(query.id, 1);
                        executionsMap[query.id] = response.data?.data?.total > 0;
                    } catch (error) {
                        console.error(`Failed to check executions for query ${query.id}:`, error);
                        executionsMap[query.id] = false;
                    }
                })
            );
            setQueryExecutionsMap(executionsMap);
        } catch (error) {
            console.error('Failed to fetch NLP queries:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleGenerate = async () => {
        if (!dbId || !question.trim()) return;
        setGenerating(true);
        try {
            await generateNLPQuery({ database_id: dbId, question });
            setQuestion('');
            // No toast notification - silently proceed
            fetchNLPQueries();
        } catch (error) {
            showToast(error.response?.data?.detail || t('page-query-generator:toast.failedGenerateQuery'), 'error');
        } finally {
            setGenerating(false);
        }
    };

    const openTestModal = (query) => {
        setSelectedQuery(query);
        // Initialize testParams with default values
        const initialParams = {};
        if (query.parameters && query.parameters.length > 0) {
            query.parameters.forEach(param => {
                initialParams[param.name] = param.default || param.example || '';
            });
        }
        setTestParams(initialParams);
        setTestModalOpen(true);
        setDetailsModalOpen(false); // Close details modal if open
        setQueryResult(null);
    };

    const handleViewChart = async (query) => {
        if (!dbId || !query.id) return;

        // If chart already exists, just navigate to charts page
        if (generatedChartQueryIds.includes(query.id)) {
            navigate(`/charts?query=${query.id}&db=${dbId}`);
            return;
        }

        setGeneratingCharts(true);
        setGeneratingChartForQuery(query.id); // Track which query is being generated
        try {
            // Generate charts for this NLP query only
            // This calls /api/chart/generate-nlp/{database_id}/{query_id}
            // which sends only ONE query to the LLM
            await generateChartsForNLPQuery(dbId, query.id);

            // No toast notification - silently proceed
            navigate(`/charts?query=${query.id}&db=${dbId}`);
        } catch (error) {
            showToast(error.response?.data?.detail || t('page-query-generator:toast.failedGenerateCharts'), 'error');
        } finally {
            setGeneratingCharts(false);
            setGeneratingChartForQuery(null); // Reset after generation completes
        }
    };

    const closeTestModal = () => {
        setTestModalOpen(false);
        setTestParams({});
        setQueryResult(null);
        setSelectedQuery(null); // Reset selected query when closing test modal
        setShowResultView('table'); // Reset to table view
    };

    const handleParamChange = (paramName, value) => {
        setTestParams(prev => ({
            ...prev,
            [paramName]: value
        }));
    };

    const handleTestQuery = async () => {
        if (!selectedQuery) return;

        setExecuting(true);
        try {
            // Use the API service to execute the query with parameters
            // Send query_id instead of the full query for security
            const response = await executeQuery({
                database_id: dbId,
                query_id: selectedQuery.id,  // Send query ID to fetch from database
                parameters: testParams       // Send parameters separately
            });

            setQueryResult(response.data);
        } catch (error) {
            console.error('Error executing query:', error);
            setQueryResult({ error: error.response?.data?.message || error.message });
        } finally {
            setExecuting(false);
        }
    };

    const openChatModal = (query) => {
        setSelectedQuery(query);
        setChatInstruction('');
        setRefinedQueryResult(null);
        setChatModalOpen(true);
        setTestModalOpen(false);
        setDetailsModalOpen(false);
    };

    // Auto-focus textarea and reset height when chat modal opens
    useEffect(() => {
        if (chatModalOpen && chatTextareaRef) {
            chatTextareaRef.focus();
            chatTextareaRef.style.height = 'auto';
        }
    }, [chatModalOpen, chatTextareaRef]);

    const closeChatModal = () => {
        setChatModalOpen(false);
        setChatInstruction('');
        setRefinedQueryResult(null);
        setRefinedQueryTestResult(null);
        setRefinedQueryTestParams({});
        setSelectedQuery(null);
        // Reset textarea height when closing
        if (chatTextareaRef) {
            chatTextareaRef.style.height = 'auto';
        }
    };

    const handleRefineQuery = async () => {
        if (!selectedQuery || !chatInstruction.trim()) return;

        setRefiningQuery(true);
        try {
            const response = await refineNLPQuery({
                query_id: selectedQuery.id,
                instruction: chatInstruction
            });

            const result = response.data;
            if (result.status === 'success') {
                const refinedParams = result.data.parameters || selectedQuery.parameters;
                const validationStatus = result.data.validation_status || 'pending';
                setRefinedQueryResult({
                    refined_query: result.data.refined_query,
                    explanation: result.data.explanation,
                    name: result.data.name || selectedQuery.name,
                    description: result.data.description || selectedQuery.description,
                    parameters: refinedParams,
                    expected_columns: result.data.expected_columns || selectedQuery.expected_columns,
                    tables_involved: result.data.tables_involved || selectedQuery.tables_involved || [],
                    validation_query: result.data.validation_query,
                    validation_status: validationStatus
                });
                // Initialize test params with defaults for convenience
                const initialTestParams = {};
                if (refinedParams && refinedParams.length > 0) {
                    refinedParams.forEach(param => {
                        initialTestParams[param.name] = param.default || param.example || '';
                    });
                }
                setRefinedQueryTestParams(initialTestParams);
                // No toast notification - silently proceed
            } else {
                showToast(result.message || t('page-query-generator:toast.failedRefineQuery'), 'error');
            }
        } catch (error) {
            console.error('Error refining query:', error);
            showToast(error.response?.data?.detail || t('page-query-generator:toast.failedRefineQuery'), 'error');
        } finally {
            setRefiningQuery(false);
        }
    };

    const handleAcceptRefinement = async () => {
        if (!selectedQuery || !refinedQueryResult) return;

        setRefiningQuery(true);
        try {
            const response = await updateNLPQuery(selectedQuery.id, {
                query: refinedQueryResult.refined_query,
                name: refinedQueryResult.name,
                description: refinedQueryResult.description,
                parameters: refinedQueryResult.parameters,
                expected_columns: refinedQueryResult.expected_columns,
                tables_involved: refinedQueryResult.tables_involved
                // Note: Not sending validation_query - backend will auto-generate it from parameters
            });

            const result = response.data;
            if (result.status === 'success') {
                // No toast notification - silently proceed
                closeChatModal();
                fetchNLPQueries();
            } else {
                showToast(result.message || t('page-query-generator:toast.failedUpdateQuery'), 'error');
            }
        } catch (error) {
            console.error('Error updating query:', error);
            showToast(error.response?.data?.detail || t('page-query-generator:toast.failedUpdateQuery'), 'error');
        } finally {
            setRefiningQuery(false);
        }
    };

    const autoResizeTextarea = (e) => {
        const textarea = e.target;
        textarea.style.height = 'auto'; // Reset to auto to allow shrinking
        textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px'; // Max height 200px
    };

    const handleChatInputChange = (e) => {
        setChatInstruction(e.target.value);
        autoResizeTextarea(e);
    };

    const handleTestRefinedQuery = async () => {
        if (!selectedQuery || !refinedQueryResult) return;

        setTestingRefinedQuery(true);
        try {
            // Test the refined query directly using the test endpoint
            const response = await testRefinedQuery({
                database_id: dbId,
                query: refinedQueryResult.refined_query,
                parameters: refinedQueryTestParams
            });

            setRefinedQueryTestResult(response.data);
            // No toast notification - silently proceed
        } catch (error) {
            console.error('Error testing refined query:', error);
            setRefinedQueryTestResult({ error: error.response?.data?.detail || error.message });
            showToast(t('page-query-generator:toast.testFailed'), 'error');
        } finally {
            setTestingRefinedQuery(false);
        }
    };

    const handleRefinedQueryTestParamChange = (paramName, value) => {
        setRefinedQueryTestParams(prev => ({
            ...prev,
            [paramName]: value
        }));
    };

    const handleViewPreviousResults = async (query) => {
        setSelectedQuery(query);
        setLoadingPreviousResults(true);
        try {
            const response = await getQueryExecutions(query.id);
            const data = response.data?.data;
            setPreviousResults(data);
            setShowPreviousResultsModal(true);
        } catch (error) {
            console.error('Failed to fetch previous results:', error);
            showToast(error.response?.data?.detail || t('page-query-generator:toast.failedFetchPreviousResults'), 'error');
        } finally {
            setLoadingPreviousResults(false);
        }
    };

    const closePreviousResultsModal = () => {
        setShowPreviousResultsModal(false);
        setPreviousResults(null);
        setSelectedQuery(null);
    };

    return (
        <div className="page-content">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="page-title">{t('page-query-generator:header.title')}</h1>
                        <p className="page-subtitle">{t('page-query-generator:header.subtitle')}</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="relative min-w-[200px]">
                            <select
                                value={dbId || ''}
                                onChange={handleDatabaseChange}
                                className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white appearance-none"
                            >
                                <option value="">{t('page-query-generator:header.selectDatabase')}</option>
                                {analyzedDatabases.map((db) => (
                                    <option key={db.id} value={db.id}>{db.name}</option>
                                ))}
                            </select>
                            <ChevronDown
                                className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                                aria-hidden
                            />
                        </div>
                    </div>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600 mb-1">{nlpQueries.length === 1 ? t('page-query-generator:stats.totalQuery') : t('page-query-generator:stats.totalQueries')}</p>
                                <p className="text-2xl font-bold text-gray-900">{nlpQueries.length}</p>
                            </div>
                            <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                                <Sparkles className="w-5 h-5 text-primary-600" />
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600 mb-1">{t('page-query-generator:stats.valueTypes')}</p>
                                <p className="text-2xl font-bold text-gray-900">
                                    {Object.keys(
                                        nlpQueries.reduce((acc, query) => {
                                            const value_type = query.value_type || 'general';
                                            acc[value_type] = true;
                                            return acc;
                                        }, {})
                                    ).length}
                                </p>
                            </div>
                            <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                                <Grid className="w-5 h-5 text-primary-600" />
                            </div>
                        </div>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-gray-600 mb-1">{t('page-query-generator:stats.database')}</p>
                                <p className="text-lg font-semibold text-gray-900 truncate">{databases.find(db => db.id === parseInt(dbId))?.name || t('page-query-generator:stats.noDatabaseSelected')}</p>
                            </div>
                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                <DatabaseIcon className="w-5 h-5 text-green-600" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Natural Language Query Input */}
                <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
                    <h3 className="text-base font-semibold text-gray-900 mb-4">{t('page-query-generator:nlQuery.heading')}</h3>

                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                {t('page-query-generator:nlQuery.askLabel')}
                            </label>
                            <textarea
                                value={question}
                                onChange={(e) => setQuestion(e.target.value)}
                                placeholder={t('page-query-generator:nlQuery.placeholder')}
                                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                                rows="3"
                            />
                        </div>

                        <button
                            onClick={handleGenerate}
                            disabled={generating || !dbId || !question.trim()}
                            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            <Sparkles className="w-4 h-4" />
                            {generating ? t('page-query-generator:generating') : t('page-query-generator:nlQuery.generate')}
                        </button>
                    </div>
                </div>

                {/* NLP Generated Queries */}
                <div className="space-y-6">
                    <h2 className="text-xl font-bold text-gray-900">{t('page-query-generator:generatedQueries.heading')}</h2>

                {!dbId ? (
                    <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                        <Database className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('page-query-generator:generatedQueries.noDbTitle')}</h3>
                        <p className="text-gray-600 mb-3">{t('page-query-generator:generatedQueries.noDbBody')}</p>
                        <p className="text-sm text-gray-500">
                            {t('page-query-generator:generatedQueries.noDbHint')}
                        </p>
                    </div>
                ) : loading ? (
                    <LoadingSpinner />
                ) : nlpQueries.length === 0 ? (
                    <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                        <p className="text-gray-500">
                            {t('page-query-generator:generatedQueries.empty')}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* Group by value_type first, then by category */}
                        {Object.entries(
                            nlpQueries.reduce((acc, query) => {
                                const value_type = query.value_type || 'general';
                                if (!acc[value_type]) acc[value_type] = {};

                                const category = query.category || 'general';
                                if (!acc[value_type][category]) acc[value_type][category] = [];

                                acc[value_type][category].push(query);
                                return acc;
                            }, {})
                        ).map(([value_type, categories]) => (
                            <div key={value_type} className="space-y-4">
                                <h2 className="text-xl font-bold text-gray-900 capitalize">{t('page-query-generator:generatedQueries.typeQueries', { type: value_type })}</h2>

                                {Object.entries(categories).map(([category, categoryQueries]) => (
                                    <div key={category} className="ml-4 space-y-4">
                                        <h3 className="text-lg font-semibold text-gray-800 capitalize">{t('page-query-generator:generatedQueries.categoryAnalytics', { category })}</h3>
                                        <p className="text-sm text-gray-600 ml-2">{t('page-query-generator:generatedQueries.queriesAvailable', { count: categoryQueries.length })}</p>

                                        <div className="space-y-3 ml-2">
                                            {categoryQueries.map((query) => (
                                                <div key={query.id} className="bg-white rounded-lg border border-gray-200 p-6">
                                                    <div className="flex items-start justify-between mb-4">
                                                        <div className="flex-1">
                                                            <h4 className="font-semibold text-gray-900 mb-1">{t('page-query-generator:queryCard.insightName', { name: query.name })}</h4>
                                                            <p className="text-sm text-gray-600 mb-2">{query.description}</p>
                                                            <div className="bg-primary-50 border border-primary-100 rounded-lg p-3">
                                                                <h5 className="font-medium text-primary-800 text-sm mb-1">{t('page-query-generator:queryCard.generatedQuery')}</h5>
                                                                <PlainQueryDisplay sql={query.query} />
                                                            </div>
                                                        </div>
                                                        <div className="flex gap-2">
                                                            {/* View Previous Results Button - Green if results exist */}
                                                            {queryExecutionsMap[query.id] && (
                                                                <button
                                                                    onClick={() => handleViewPreviousResults(query)}
                                                                    className="px-3 py-1.5 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700 transition-colors flex items-center gap-1 whitespace-nowrap"
                                                                    title={t('page-query-generator:queryCard.viewPreviousResultsTitle')}
                                                                >
                                                                    <History className="w-3 h-3" />
                                                                    {t('page-query-generator:queryCard.previousResults')}
                                                                </button>
                                                            )}
                                                            <button
                                                                onClick={() => openTestModal(query)}
                                                                className="px-3 py-1.5 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700 transition-colors flex items-center gap-1 whitespace-nowrap"
                                                                title={t('page-query-generator:queryCard.testQueryTitle')}
                                                            >
                                                                <Play className="w-3 h-3" />
                                                                {t('page-query-generator:queryCard.test')}
                                                            </button>
                                                            <button
                                                                onClick={() => openChatModal(query)}
                                                                className="px-3 py-1.5 bg-primary-600 text-white rounded text-xs font-medium hover:bg-primary-700 transition-colors flex items-center gap-1 whitespace-nowrap"
                                                                title={t('page-query-generator:queryCard.chatToRefineTitle')}
                                                            >
                                                                <MessageSquare className="w-3 h-3" />
                                                                {t('page-query-generator:queryCard.chat')}
                                                            </button>
                                                            <button
                                                                onClick={() => {
                                                                    setTestModalOpen(false); // Close test modal if open
                                                                    setSelectedQuery(query);
                                                                    setDetailsModalOpen(true); // Open details modal
                                                                }}
                                                                className="ml-2 px-3 py-1.5 bg-primary-600 text-white rounded text-xs font-medium hover:bg-primary-700 transition-colors flex items-center gap-1 whitespace-nowrap"
                                                            >
                                                                <Eye className="w-3 h-3" />
                                                                {t('page-query-generator:queryCard.viewDetails')}
                                                            </button>
                                                            {/* Show Generate/View Chart button only after query generation is complete */}
                                                            {query.id && (
                                                                <button
                                                                    onClick={() => generatedChartQueryIds.includes(query.id) ? navigate(`/charts?query=${query.id}&db=${dbId}`) : handleViewChart(query)}
                                                                    disabled={generatingCharts && generatingChartForQuery === query.id}
                                                                    className={`ml-2 px-3 py-1.5 rounded text-xs font-medium transition-colors flex items-center gap-1 whitespace-nowrap ${
                                                                        generatedChartQueryIds.includes(query.id)
                                                                            ? 'bg-primary-600 text-white hover:bg-primary-700'
                                                                            : generatingChartForQuery === query.id
                                                                                ? 'bg-primary-400 text-white cursor-not-allowed'
                                                                                : 'bg-primary-600 text-white hover:bg-primary-700'
                                                                    }`}
                                                                    title={
                                                                        generatedChartQueryIds.includes(query.id)
                                                                            ? t('page-query-generator:queryCard.viewChartTitle')
                                                                            : generatingChartForQuery === query.id
                                                                                ? t('page-query-generator:queryCard.generatingChartTitle')
                                                                                : t('page-query-generator:queryCard.generateChartTitle')
                                                                    }
                                                                >
                                                                    <BarChart3 className={`w-3 h-3 ${generatingChartForQuery === query.id ? 'animate-spin' : ''}`} />
                                                                    {generatedChartQueryIds.includes(query.id)
                                                                        ? t('page-query-generator:queryCard.viewChart')
                                                                        : generatingChartForQuery === query.id
                                                                            ? t('page-query-generator:generating')
                                                                            : t('page-query-generator:queryCard.generateChart')}
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>

                                                    <div className="mt-4 grid grid-cols-2 gap-4 text-xs">
                                                        <div>
                                                            <span className="font-semibold text-gray-700">{t('page-query-generator:queryCard.validationStatusLabel')}</span>
                                                            <span className={`ml-1 px-2 py-0.5 rounded ${query.validation_status === 'valid'
                                                                    ? 'bg-green-100 text-green-700'
                                                                    : 'bg-red-100 text-red-700'
                                                                }`}>
                                                                {query.validation_status || t('common:unknown')}
                                                            </span>
                                                        </div>
                                                        <div>
                                                            <span className="font-semibold text-gray-700">{t('page-query-generator:queryCard.dbDialectLabel')}</span>
                                                            <span className="ml-1 px-2 py-0.5 bg-primary-100 text-primary-700 rounded">
                                                                {query.db_dialect || t('page-query-generator:queryCard.na')}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    {query.value_description && (
                                                        <div className="mt-3 p-3 bg-primary-50 rounded-lg border border-primary-100">
                                                            <p className="text-sm text-primary-900">
                                                                <span className="font-semibold">{t('page-query-generator:queryCard.businessValueLabel')}</span> {query.value_description}
                                                            </p>
                                                        </div>
                                                    )}

                                                    {query.parameters && query.parameters.length > 0 && (
                                                        <div className="mt-3">
                                                            <h5 className="font-medium text-gray-900 mb-2">{t('page-query-generator:queryCard.parametersHeading')}</h5>
                                                            <div className="grid grid-cols-2 gap-2 text-xs">
                                                                {query.parameters.map((param, idx) => (
                                                                    <div key={idx} className="bg-gray-50 p-2 rounded">
                                                                        <div className="font-semibold">{param.name} ({param.type})</div>
                                                                        <div>{param.description}</div>
                                                                        {param.default && <div className="text-gray-500">{t('page-query-generator:queryCard.defaultValue', { value: param.default })}</div>}
                                                                        {param.example && <div className="text-gray-500">{t('page-query-generator:queryCard.exampleValue', { value: param.example })}</div>}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    <div className="mt-3 text-xs text-gray-500">
                                                        {t('page-query-generator:queryCard.createdLabel', { date: formatLocalTime(query.created_at) })}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                )}
                </div>
                
                {/* Query Details Modal */}
                {selectedQuery && detailsModalOpen && (  // Show only if details modal is open
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg max-w-3xl w-full max-h-[80vh] overflow-auto">
                            <div className="p-6">
                                <h3 className="text-xl font-bold mb-4">{selectedQuery.name}</h3>
                                <p className="text-sm text-gray-600 mb-4">{selectedQuery.description}</p>

                                <div className="space-y-4">
                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-2">{t('page-query-generator:detailsModal.queryDetailsHeading')}</h4>
                                        <div className="grid grid-cols-2 gap-4 text-sm">
                                            <div>
                                                <p><span className="font-medium">{t('page-query-generator:queryCard.validationStatusLabel')}</span> {selectedQuery.validation_status || t('page-query-generator:queryCard.na')}</p>
                                                <p><span className="font-medium">{t('page-query-generator:queryCard.dbDialectLabel')}</span> {selectedQuery.db_dialect || t('page-query-generator:queryCard.na')}</p>
                                                <p><span className="font-medium">{t('page-query-generator:detailsModal.valueTypeLabel')}</span> {selectedQuery.value_type || t('page-query-generator:queryCard.na')}</p>
                                            </div>
                                            <div>
                                                <p><span className="font-medium">{t('page-query-generator:detailsModal.validationResultLabel')}</span> {selectedQuery.validation_result || t('common:none')}</p>
                                                <p><span className="font-medium">{t('page-query-generator:detailsModal.sourceLabel')}</span> {selectedQuery.source || t('page-query-generator:queryCard.na')}</p>
                                                <p><span className="font-medium">{t('page-query-generator:detailsModal.createdAtLabel')}</span> {formatLocalTime(selectedQuery.created_at)}</p>
                                            </div>
                                        </div>
                                        {selectedQuery.suggested_chart && (
                                            <div className="mt-3 p-3 bg-primary-50 border border-primary-100 rounded-lg">
                                                <div className="flex items-start gap-2">
                                                    <BarChart3 className="w-4 h-4 text-primary-600 mt-0.5" />
                                                    <div>
                                                        <p className="text-sm font-medium text-primary-900">
                                                            {t('page-query-generator:detailsModal.suggestedChart', { type: selectedQuery.suggested_chart.type?.toUpperCase() })}
                                                        </p>
                                                        {selectedQuery.suggested_chart.reason && (
                                                            <p className="text-xs text-primary-700 mt-1">
                                                                {selectedQuery.suggested_chart.reason}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {selectedQuery.value_description && (
                                        <div>
                                            <h4 className="font-semibold text-gray-900 mb-2">{t('page-query-generator:detailsModal.businessValueHeading')}</h4>
                                            <p>{selectedQuery.value_description}</p>
                                        </div>
                                    )}

                                    <div>
                                        <h4 className="font-semibold text-gray-900 mb-2">{t('page-query-generator:queryCard.generatedQuery')}</h4>
                                        <PlainQueryDisplay sql={selectedQuery.query} />
                                    </div>

                                    {selectedQuery.parameters && selectedQuery.parameters.length > 0 && (
                                        <div>
                                            <h4 className="font-semibold text-gray-900 mb-2">{t('page-query-generator:queryCard.parametersHeading')}</h4>
                                            <div className="space-y-2">
                                                {selectedQuery.parameters.map((param, idx) => (
                                                    <div key={idx} className="border border-gray-200 rounded p-2">
                                                        <div className="font-semibold">{param.name} ({param.type})</div>
                                                        <div className="text-sm">{param.description}</div>
                                                        {param.default && <div className="text-sm text-gray-500">{t('page-query-generator:queryCard.defaultValue', { value: param.default })}</div>}
                                                        {param.example && <div className="text-sm text-gray-500">{t('page-query-generator:queryCard.exampleValue', { value: param.example })}</div>}
                                                        <div className="text-sm text-gray-500">{t('page-query-generator:detailsModal.requiredLabel', { value: param.required ? t('common:yes') : t('common:no') })}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {selectedQuery.expected_columns && selectedQuery.expected_columns.length > 0 && (
                                        <div>
                                            <h4 className="font-semibold text-gray-900 mb-2">{t('page-query-generator:detailsModal.expectedColumnsHeading')}</h4>
                                            <div className="flex flex-wrap gap-2">
                                                {selectedQuery.expected_columns.map((col, idx) => (
                                                    <span key={idx} className="px-3 py-1 bg-gray-100 text-gray-700 rounded text-sm">
                                                        {col}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {selectedQuery.tables_involved && selectedQuery.tables_involved.length > 0 && (
                                        <div className="mt-4">
                                            <h4 className="font-semibold text-gray-900 mb-2">Tables Involved</h4>
                                            <div className="flex flex-wrap gap-2">
                                                {selectedQuery.tables_involved.map((table, idx) => (
                                                    <span key={idx} className="px-3 py-1 bg-primary-100 text-primary-700 rounded text-sm font-medium">
                                                        {table}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="mt-6 flex justify-end gap-2">
                                    <button
                                        onClick={() => setDetailsModalOpen(false)}  // Close details modal
                                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Test Query Modal */}
                {testModalOpen && selectedQuery && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] min-h-[40vh] h-auto overflow-auto">
                            <div className="p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-xl font-bold">Test Query: {selectedQuery.name}</h3>
                                    <button
                                        onClick={closeTestModal}
                                        className="p-1 text-gray-500 hover:text-gray-700"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>

                                <div className="mb-4">
                                    <div className="flex items-center gap-3 mb-2">
                                        <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
                                            QUERY
                                        </span>
                                        <div>
                                            <h4 className="font-semibold text-gray-900">{selectedQuery.name}</h4>
                                        </div>
                                    </div>
                                    <p className="text-sm text-gray-600">{selectedQuery.description}</p>
                                </div>

                                <div className="mb-6">
                                    <h4 className="font-semibold text-gray-900 mb-2">Query Preview</h4>
                                    <PlainQueryDisplay sql={selectedQuery.query} />
                                </div>

                                {selectedQuery.parameters && selectedQuery.parameters.length > 0 && (
                                    <div className="mb-6">
                                        <h4 className="font-semibold text-gray-900 mb-3">Parameters</h4>
                                        <div className="space-y-4">
                                            {selectedQuery.parameters.map((param, idx) => {
                                                return (
                                                    <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
                                                        <div className="md:col-span-3">
                                                            <label className="block text-sm font-medium text-gray-700">
                                                                {param.name}
                                                                {param.required && <span className="text-red-500"> *</span>}
                                                            </label>
                                                        </div>
                                                        <div className="md:col-span-6">
                                                            {param.type === 'boolean' ? (
                                                                <input
                                                                    type="checkbox"
                                                                    checked={testParams[param.name] === true || testParams[param.name] === 'true'}
                                                                    onChange={(e) => handleParamChange(param.name, e.target.checked)}
                                                                    className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
                                                                />
                                                            ) : param.type === 'date' || param.type === 'datetime' ? (
                                                                <input
                                                                    type="date"
                                                                    value={testParams[param.name] || ''}
                                                                    onChange={(e) => handleParamChange(param.name, e.target.value)}
                                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                                                />
                                                            ) : (
                                                                <input
                                                                    type="text"
                                                                    value={testParams[param.name] || ''}
                                                                    onChange={(e) => handleParamChange(param.name, e.target.value)}
                                                                    placeholder={param.default ? `Default: ${param.default}` : param.example ? `Example: ${param.example}` : `Enter ${param.name}`}
                                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                                                />
                                                            )}
                                                        </div>
                                                        <div className="md:col-span-3 text-sm text-gray-500">
                                                            {param.description || 'Parameter description'}
                                                            <div className="mt-1 text-xs text-gray-400">
                                                                {param.type || 'string'} (query)
                                                                {param.required ? ' - required' : ' - optional'}
                                                            </div>
                                                            {param.default && (
                                                                <div className="mt-1 text-xs text-gray-400">
                                                                    Default: {param.default}
                                                                </div>
                                                            )}
                                                            {param.example && (
                                                                <div className="mt-1 text-xs text-gray-400">
                                                                    Example: {param.example}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                <div className="flex justify-between items-center mb-4">
                                    <button
                                        onClick={() => {
                                            // Reset to default values
                                            const defaultParams = {};
                                            if (selectedQuery.parameters && selectedQuery.parameters.length > 0) {
                                                selectedQuery.parameters.forEach(param => {
                                                    defaultParams[param.name] = param.default || param.example || '';
                                                });
                                            }
                                            setTestParams(defaultParams);
                                            setQueryResult(null);
                                        }}
                                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
                                    >
                                        Clear All
                                    </button>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={closeTestModal}
                                            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleTestQuery}
                                            disabled={executing}
                                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                        >
                                            <Play className="w-4 h-4" />
                                            {executing ? 'Executing...' : 'Execute Query'}
                                        </button>
                                    </div>
                                </div>

                                {queryResult && (
                                    <div className="mt-6">
                                        <div className="flex items-center justify-between mb-3">
                                            <h4 className="font-semibold text-gray-900">Results</h4>
                                            <div className="flex space-x-4">
                                                <label className="inline-flex items-center">
                                                    <input
                                                        type="radio"
                                                        className="form-radio text-primary-600"
                                                        checked={showResultView === 'table'}
                                                        onChange={() => setShowResultView('table')}
                                                    />
                                                    <span className="ml-2 text-sm">Table View</span>
                                                </label>
                                                <label className="inline-flex items-center">
                                                    <input
                                                        type="radio"
                                                        className="form-radio text-primary-600"
                                                        checked={showResultView === 'json'}
                                                        onChange={() => setShowResultView('json')}
                                                    />
                                                    <span className="ml-2 text-sm">JSON View</span>
                                                </label>
                                            </div>
                                        </div>

                                        <div className="border border-gray-200 rounded-lg p-4 max-h-96 overflow-auto">
                                            {queryResult.error ? (
                                                <div className="text-sm text-red-600">
                                                    <span className="font-semibold">Error:</span> {queryResult.error}
                                                </div>
                                            ) : showResultView === 'table' ? (
                                                <div className="overflow-x-auto">
                                                    {(() => {
                                                        // Handle API response structure: { data: { rows: [...], columns: [...] } }
                                                        const resultData = queryResult.data;
                                                        const rows = resultData?.rows || resultData?.data?.rows || [];
                                                        const columns = resultData?.columns || resultData?.data?.columns || [];

                                                        if (rows && rows.length > 0 && columns && columns.length > 0) {
                                                            return (
                                                                <table className="min-w-full divide-y divide-gray-200">
                                                                    <thead className="bg-gray-50">
                                                                        <tr>
                                                                            {columns.map((col, idx) => (
                                                                                <th key={idx} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                                    {col}
                                                                                </th>
                                                                            ))}
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody className="bg-white divide-y divide-gray-200">
                                                                        {rows.map((row, rowIndex) => (
                                                                            <tr key={rowIndex}>
                                                                                {columns.map((col, colIndex) => (
                                                                                    <td key={colIndex} className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                                                                        {row[col] === null ? (
                                                                                            <span className="text-gray-400 italic">NULL</span>
                                                                                        ) : typeof row[col] === 'object' ? (
                                                                                            <pre className="text-xs">{JSON.stringify(row[col], null, 2)}</pre>
                                                                                        ) : (
                                                                                            String(row[col])
                                                                                        )}
                                                                                    </td>
                                                                                ))}
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            );
                                                        }
                                                        return (
                                                            <div className="text-sm text-gray-500">
                                                                {queryResult.status === 'success'
                                                                    ? 'No data returned from the query.'
                                                                    : 'The response does not contain structured data to display in a table.'}
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            ) : (
                                                <pre className="text-xs overflow-auto max-h-96">
                                                    {JSON.stringify(queryResult, null, 2)}
                                                </pre>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Chat Refinement Modal */}
                {chatModalOpen && selectedQuery && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto">
                            <div className="p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-xl font-bold flex items-center gap-2">
                                        <MessageSquare className="w-5 h-5 text-primary-600" />
                                        Refine Query with Chat
                                    </h3>
                                    <button
                                        onClick={closeChatModal}
                                        className="p-1 text-gray-500 hover:text-gray-700"
                                    >
                                        <X className="w-5 h-5" />
                                    </button>
                                </div>

                                {/* Query Info */}
                                <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                                    <h4 className="font-semibold text-gray-900 mb-2">{selectedQuery.name}</h4>
                                    <p className="text-sm text-gray-600 mb-3">{selectedQuery.description}</p>
                                    <PlainQueryDisplay sql={selectedQuery.query} />
                                </div>

                                {/* Chat Input */}
                                <div className="mb-4">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        What would you like to change?
                                    </label>
                                    <textarea
                                        ref={(el) => setChatTextareaRef(el)}
                                        value={chatInstruction}
                                        onChange={handleChatInputChange}
                                        placeholder="Examples:
- Change the date range to last 30 days
- Add a filter for active users only
- Group by month instead of week
- Show only the top 10 results
- Add an ORDER BY clause to sort by revenue"
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm resize-none overflow-y-auto"
                                        rows="6"
                                        style={{ minHeight: '144px', maxHeight: '200px' }}
                                        disabled={refiningQuery}
                                    />
                                </div>

                                {/* Action Buttons */}
                                <div className="flex justify-end gap-2 mb-4">
                                    <button
                                        onClick={closeChatModal}
                                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
                                        disabled={refiningQuery}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleRefineQuery}
                                        disabled={refiningQuery || !chatInstruction.trim()}
                                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                    >
                                        <MessageSquare className="w-4 h-4" />
                                        {refiningQuery ? 'Refining...' : 'Refine Query'}
                                    </button>
                                </div>

                                {/* Refined Query Result */}
                                {refinedQueryResult && (
                                    <div className="border-t border-gray-200 pt-4">
                                        {/* Refined Query Display */}
                                        <div className="mb-4">
                                            <h4 className="font-semibold text-gray-900 mb-2">Refined Query</h4>
                                            <div className="bg-gray-50 border border-gray-200 rounded p-3 font-mono text-xs text-gray-800 overflow-x-auto">
                                                <pre className="whitespace-pre-wrap">{refinedQueryResult.refined_query}</pre>
                                            </div>
                                        </div>

                                        {/* Show updated fields if they changed */}
                                        {refinedQueryResult.name !== selectedQuery.name && (
                                            <div className="mb-4">
                                                <h4 className="font-semibold text-gray-900 mb-2">Updated Name</h4>
                                                <p className="text-sm text-gray-700 bg-gray-50 p-2 rounded">{refinedQueryResult.name}</p>
                                            </div>
                                        )}

                                        {refinedQueryResult.description !== selectedQuery.description && (
                                            <div className="mb-4">
                                                <h4 className="font-semibold text-gray-900 mb-2">Updated Description</h4>
                                                <p className="text-sm text-gray-700 bg-gray-50 p-2 rounded">{refinedQueryResult.description}</p>
                                            </div>
                                        )}

                                        {refinedQueryResult.parameters && refinedQueryResult.parameters.length > 0 && (
                                            <div className="mb-4">
                                                <h4 className="font-semibold text-gray-900 mb-2">Parameters</h4>
                                                <div className="grid grid-cols-2 gap-2 text-xs">
                                                    {refinedQueryResult.parameters.map((param, idx) => (
                                                        <div key={idx} className="bg-gray-50 p-2 rounded border">
                                                            <div className="font-semibold">{param.name} ({param.type})</div>
                                                            <div className="text-gray-600">{param.description}</div>
                                                            {param.default && <div className="text-gray-500">Default: {param.default}</div>}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {refinedQueryResult.expected_columns && refinedQueryResult.expected_columns.length > 0 && (
                                            <div className="mb-4">
                                                <h4 className="font-semibold text-gray-900 mb-2">Expected Columns</h4>
                                                <div className="flex flex-wrap gap-2">
                                                    {refinedQueryResult.expected_columns.map((col, idx) => (
                                                        <span key={idx} className="px-2 py-1 bg-primary-50 text-primary-700 rounded text-xs border border-primary-200">
                                                            {col}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {refinedQueryResult.tables_involved && refinedQueryResult.tables_involved.length > 0 && (
                                            <div className="mb-4">
                                                <h4 className="font-semibold text-gray-900 mb-2">Tables Involved</h4>
                                                <div className="flex flex-wrap gap-2">
                                                    {refinedQueryResult.tables_involved.map((table, idx) => (
                                                        <span key={idx} className="px-2 py-1 bg-primary-50 text-primary-700 rounded text-xs border border-primary-200 font-medium">
                                                            {table}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {/* Parameters for Testing */}
                                        {refinedQueryResult.parameters && refinedQueryResult.parameters.length > 0 && (
                                            <div className="mb-4">
                                                <h4 className="font-semibold text-gray-900 mb-3">Test Parameters</h4>
                                                <div className="space-y-3">
                                                    {refinedQueryResult.parameters.map((param, idx) => {
                                                        return (
                                                            <div key={idx} className="grid grid-cols-12 gap-4 items-center">
                                                                <div className="col-span-3">
                                                                    <label className="block text-sm font-medium text-gray-700">
                                                                        {param.name}
                                                                        {param.required && <span className="text-red-500"> *</span>}
                                                                    </label>
                                                                </div>
                                                                <div className="col-span-6">
                                                                    {param.type === 'boolean' ? (
                                                                        <input
                                                                            type="checkbox"
                                                                            checked={refinedQueryTestParams[param.name] === true || refinedQueryTestParams[param.name] === 'true'}
                                                                            onChange={(e) => handleRefinedQueryTestParamChange(param.name, e.target.checked)}
                                                                            className="w-5 h-5 text-primary-600 rounded focus:ring-primary-500"
                                                                        />
                                                                    ) : param.type === 'date' || param.type === 'datetime' ? (
                                                                        <input
                                                                            type="date"
                                                                            value={param.name in refinedQueryTestParams ? refinedQueryTestParams[param.name] : (param.default || param.example || '')}
                                                                            onChange={(e) => handleRefinedQueryTestParamChange(param.name, e.target.value)}
                                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                                                        />
                                                                    ) : (
                                                                        <input
                                                                            type="text"
                                                                            value={param.name in refinedQueryTestParams ? refinedQueryTestParams[param.name] : (param.default || param.example || '')}
                                                                            onChange={(e) => handleRefinedQueryTestParamChange(param.name, e.target.value)}
                                                                            placeholder={param.default ? `Default: ${param.default}` : param.example ? `Example: ${param.example}` : `Enter ${param.name}`}
                                                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                                                        />
                                                                    )}
                                                                </div>
                                                                <div className="col-span-3 text-sm text-gray-500">
                                                                    {param.description || 'Parameter'}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        {/* Action Buttons */}
                                        <div className="flex justify-between items-center mb-4 pt-4 border-t border-gray-200">
                                            <button
                                                onClick={() => {
                                                    // Reset to default values
                                                    const defaultParams = {};
                                                    if (refinedQueryResult.parameters && refinedQueryResult.parameters.length > 0) {
                                                        refinedQueryResult.parameters.forEach(param => {
                                                            defaultParams[param.name] = param.default || param.example || '';
                                                        });
                                                    }
                                                    setRefinedQueryTestParams(defaultParams);
                                                    setRefinedQueryTestResult(null);
                                                }}
                                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
                                            >
                                                Clear All
                                            </button>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => {
                                                        setRefinedQueryResult(null);
                                                        setRefinedQueryTestParams({});
                                                        setRefinedQueryTestResult(null);
                                                        // Keep chatInstruction persistent so user can retry or modify
                                                        // setChatInstruction('');
                                                    }}
                                                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
                                                >
                                                    Start Over
                                                </button>
                                                <button
                                                    onClick={handleTestRefinedQuery}
                                                    disabled={testingRefinedQuery}
                                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                                >
                                                    <Play className="w-4 h-4" />
                                                    {testingRefinedQuery ? 'Executing...' : 'Execute'}
                                                </button>
                                            </div>
                                        </div>

                                        {/* Test Results */}
                                        {refinedQueryTestResult && (
                                            <div className="mb-4 border-t border-gray-200 pt-4">
                                                <div className="flex items-center justify-between mb-3">
                                                    <h4 className="font-semibold text-gray-900">Test Results</h4>
                                                    <div className="flex space-x-4">
                                                        <label className="inline-flex items-center">
                                                            <input
                                                                type="radio"
                                                                className="form-radio text-primary-600"
                                                                checked={showRefinedQueryTestResult === 'table'}
                                                                onChange={() => setShowRefinedQueryTestResult('table')}
                                                            />
                                                            <span className="ml-2 text-sm">Table View</span>
                                                        </label>
                                                        <label className="inline-flex items-center">
                                                            <input
                                                                type="radio"
                                                                className="form-radio text-primary-600"
                                                                checked={showRefinedQueryTestResult === 'json'}
                                                                onChange={() => setShowRefinedQueryTestResult('json')}
                                                            />
                                                            <span className="ml-2 text-sm">JSON View</span>
                                                        </label>
                                                    </div>
                                                </div>

                                                <div className="border border-gray-200 rounded-lg p-4 max-h-96 overflow-auto">
                                                    {refinedQueryTestResult.error ? (
                                                        <div className="text-sm text-red-600">
                                                            <span className="font-semibold">Error:</span> {refinedQueryTestResult.error}
                                                        </div>
                                                    ) : showRefinedQueryTestResult === 'table' ? (
                                                        <div className="overflow-x-auto">
                                                            {(() => {
                                                                // Handle API response structure: { data: { rows: [...], columns: [...] } }
                                                                const resultData = refinedQueryTestResult.data;
                                                                const rows = resultData?.rows || resultData?.data?.rows || [];
                                                                const columns = resultData?.columns || resultData?.data?.columns || [];

                                                                if (rows && rows.length > 0 && columns && columns.length > 0) {
                                                                    return (
                                                                        <table className="min-w-full divide-y divide-gray-200">
                                                                            <thead className="bg-gray-50">
                                                                                <tr>
                                                                                    {columns.map((col, idx) => (
                                                                                        <th key={idx} className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                                            {col}
                                                                                        </th>
                                                                                    ))}
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody className="bg-white divide-y divide-gray-200">
                                                                                {rows.map((row, rowIndex) => (
                                                                                    <tr key={rowIndex}>
                                                                                        {columns.map((col, colIndex) => (
                                                                                            <td key={colIndex} className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                                                                                                {row[col] === null ? (
                                                                                                    <span className="text-gray-400 italic">NULL</span>
                                                                                                ) : typeof row[col] === 'object' ? (
                                                                                                    <pre className="text-xs">{JSON.stringify(row[col], null, 2)}</pre>
                                                                                                ) : (
                                                                                                    String(row[col])
                                                                                                )}
                                                                                            </td>
                                                                                        ))}
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                    );
                                                                }
                                                                return (
                                                                    <div className="text-sm text-gray-500">
                                                                        {refinedQueryTestResult.status === 'success'
                                                                            ? 'No data returned from the query.'
                                                                            : 'The response does not contain structured data to display in a table.'}
                                                                    </div>
                                                                );
                                                            })()}
                                                        </div>
                                                    ) : (
                                                        <pre className="text-xs overflow-auto max-h-96">
                                                            {JSON.stringify(refinedQueryTestResult, null, 2)}
                                                        </pre>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Accept and Save Button - Below Test Results */}
                                        <div className="flex justify-end pt-4 border-t border-gray-200">
                                            <button
                                                onClick={handleAcceptRefinement}
                                                disabled={refiningQuery || testingRefinedQuery}
                                                className="px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                                            >
                                                <Check className="w-4 h-4" />
                                                {refiningQuery ? 'Saving...' : 'Accept and Save Query'}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Previous Results Modal */}
                {showPreviousResultsModal && selectedQuery && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-auto">
                            <div className="p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <div>
                                        <h3 className="text-xl font-bold flex items-center gap-2">
                                            <History className="w-6 h-6 text-green-600" />
                                            Previous Results: {selectedQuery.name}
                                        </h3>
                                        <p className="text-sm text-gray-600 mt-1">{selectedQuery.description}</p>
                                    </div>
                                    <button
                                        onClick={closePreviousResultsModal}
                                        className="p-1 text-gray-500 hover:text-gray-700"
                                    >
                                        <X className="w-6 h-6" />
                                    </button>
                                </div>

                                {loadingPreviousResults ? (
                                    <div className="flex items-center justify-center py-12">
                                        <LoadingSpinner />
                                    </div>
                                ) : previousResults && previousResults.executions && previousResults.executions.length > 0 ? (
                                    <div className="space-y-6">
                                        {/* Query Info */}
                                        <div className="bg-gray-50 rounded-lg p-4">
                                            <h4 className="font-semibold text-gray-900 mb-2">Query</h4>
                                            <PlainQueryDisplay sql={selectedQuery.query} />
                                        </div>

                                        {/* Execution History */}
                                        <div>
                                            <h4 className="font-semibold text-gray-900 mb-3">
                                                Execution History ({previousResults.total} {previousResults.total === 1 ? 'execution' : 'executions'})
                                            </h4>
                                            <div className="space-y-4">
                                                {previousResults.executions.map((execution, idx) => (
                                                    <div key={execution.id} className="border border-gray-200 rounded-lg overflow-hidden">
                                                        {/* Execution Header */}
                                                        <div className={`px-4 py-3 border-b ${
                                                            execution.status === 'success' 
                                                                ? 'bg-green-50 border-green-200' 
                                                                : 'bg-red-50 border-red-200'
                                                        }`}>
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center gap-3">
                                                                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                                                                        execution.status === 'success'
                                                                            ? 'bg-green-200 text-green-800'
                                                                            : 'bg-red-200 text-red-800'
                                                                    }`}>
                                                                        {execution.status?.toUpperCase()}
                                                                    </span>
                                                                    <span className="text-sm font-medium text-gray-900">
                                                                        Execution #{previousResults.total - idx}
                                                                    </span>
                                                                </div>
                                                                <div className="text-sm text-gray-600">
                                                                    {new Date(execution.execution_time).toLocaleString()}
                                                                </div>
                                                            </div>
                                                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-600">
                                                                <span>Duration: {execution.execution_duration_ms}ms</span>
                                                                <span>Rows: {execution.row_count || 0}</span>
                                                            </div>
                                                        </div>

                                                        {/* Execution Details */}
                                                        <div className="p-4 bg-white">
                                                            {/* Parameters Used */}
                                                            {execution.parameters_used && Object.keys(execution.parameters_used).length > 0 && (
                                                                <div className="mb-3">
                                                                    <h5 className="font-medium text-gray-900 mb-2 text-sm">Parameters Used</h5>
                                                                    <div className="flex flex-wrap gap-2">
                                                                        {Object.entries(execution.parameters_used).map(([key, value]) => (
                                                                            <span key={key} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                                                                                {key}: {String(value)}
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Error Details */}
                                                            {execution.error_details && (
                                                                <div className="mb-3">
                                                                    <h5 className="font-medium text-red-900 mb-2 text-sm">Error</h5>
                                                                    <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">
                                                                        {execution.error_details}
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Result Preview */}
                                                            {execution.result_preview && (
                                                                <div>
                                                                    <h5 className="font-medium text-gray-900 mb-2 text-sm">Result Preview</h5>
                                                                    <div className="bg-gray-50 border border-gray-200 rounded p-3 overflow-x-auto">
                                                                        <pre className="text-xs text-gray-800">
                                                                            {typeof execution.result_preview === 'string' 
                                                                                ? execution.result_preview 
                                                                                : JSON.stringify(execution.result_preview, null, 2)}
                                                                        </pre>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-12">
                                        <History className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                                        <h4 className="text-lg font-semibold text-gray-900 mb-2">No Previous Results</h4>
                                        <p className="text-gray-600">This query hasn't been executed yet.</p>
                                    </div>
                                )}

                                <div className="mt-6 flex justify-end gap-2">
                                    <button
                                        onClick={closePreviousResultsModal}
                                        className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
        </div>
    );
};
